/**
 * FileUploadManager - Simple file input display manager
 *
 * Just shows selected files in a list, lets the native HTML5 file input handle everything else
 */

export default class FileUploadManagerCart {
    private fileInput: HTMLInputElement | null;
    private uploadList: HTMLElement | null;
    private allFiles: File[] = []; // Keep track of all selected files

    constructor() {
        this.fileInput = document.getElementById('file-upload') as HTMLInputElement;
        this.uploadList = document.querySelector('.base-cart-form__upload-list') as HTMLElement;

        if (this.fileInput && this.uploadList) {
            this.initEvents();
            this.clearExampleFiles();
        }
    }

    private initEvents(): void {
        if (!this.fileInput) return;

        // Listen to file input changes and add new files to existing ones
        this.fileInput.addEventListener('change', (event) => {
            this.handleFileSelection(event);
        });
    }

    private clearExampleFiles(): void {
        if (!this.uploadList) return;

        // Clear any existing items
        this.uploadList.innerHTML = '';
    }

    private handleFileSelection(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (!target.files || target.files.length === 0) return;

        // Get newly selected files
        const newFiles = Array.from(target.files);

        // Přidání nových souborů, ale maximálně 3
        newFiles.forEach(newFile => {
            // Check if file already exists (by name and size)
            const exists = this.allFiles.some(existingFile =>
                existingFile.name === newFile.name && existingFile.size === newFile.size
            );

            if (!exists && this.allFiles.length < 3) {
                this.allFiles.push(newFile);
            }
        });

        // Omezit pole na max 3 soubory (pro případ, že by uživatel použil více najednou)
        if (this.allFiles.length > 3) {
            this.allFiles = this.allFiles.slice(0, 3);
        }

        // Update the file input with all files
        const dt = new DataTransfer();
        this.allFiles.forEach(file => dt.items.add(file));
        this.fileInput!.files = dt.files;

        // Display all files
        this.displaySelectedFiles();
    }

    private displaySelectedFiles(): void {
        if (!this.fileInput || !this.uploadList) return;

        // Clear previous list
        this.uploadList.innerHTML = '';

        // Display all selected files
        if (this.fileInput.files) {
            Array.from(this.fileInput.files).forEach((file, index) => {
                this.createFileElement(file, index);
            });
        }
    }

    private createFileElement(file: File, index: number): void {
        if (!this.uploadList) return;

        const fileElement = document.createElement('div');
        fileElement.className = 'base-modal-upload__item';

        // Pokud je obrázek, zobrazíme náhled
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = document.createElement('img');
                img.src = e.target?.result as string;
                img.alt = file.name;
                img.className = 'base-modal-upload__img';
                fileElement.appendChild(img);
            };
            reader.readAsDataURL(file);
        }

        // Odebrání souboru
        const remover = document.createElement('a');
        remover.href = 'javascript:';
        remover.className = 'base-modal-upload__remover';
        remover.innerHTML = `<img src="${(window as any).basePath || ''}/img/icon/upload-remover-icon.svg" alt="ICON: Upload remover">`;
        remover.addEventListener('click', () => {
            this.removeFile(index);
        });
        fileElement.insertBefore(remover, fileElement.firstChild);

        this.uploadList.appendChild(fileElement);
    }

    private removeFile(indexToRemove: number): void {
        if (!this.fileInput) return;

        // Remove file from our internal array
        this.allFiles.splice(indexToRemove, 1);

        // Update the file input with remaining files
        const dt = new DataTransfer();
        this.allFiles.forEach(file => dt.items.add(file));
        this.fileInput.files = dt.files;

        // Refresh the display
        this.displaySelectedFiles();
    }

    private getFileNameWithoutExtension(filename: string): string {
        return filename.substring(0, filename.lastIndexOf('.')) || filename;
    }

    private getFileExtension(filename: string): string {
        const extension = filename.substring(filename.lastIndexOf('.'));
        return extension || '';
    }

    // Static method for initialization
    public static init(): void {
        new FileUploadManagerCart();
    }
}
